<script>
import { mapActions } from "pinia";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import isEmpty from "lodash/isEmpty";
import { useMainStore } from "../stores/mainStore";
import GoogleMap from "./GoogleMap.vue";
import RadioInput from "./forms/RadioInput.vue";
import InputField from "@/components/forms/InputField.vue";
import { validateUKPostcode } from "@/components/methods/index.js";

export default {
    name: "AutocompleteWithMap",
    components: {
        VueGoogleAutocomplete,
        GoogleMap,
        RadioInput,
        InputField,
    },
    emits: [
        "venueAdded",
        "submitEditVenueModal",
        "openEditVenueModal",
        "enterManuallyChanged",
        "validatedSummaryFormNumErrors",
        "venueTypeChange",
        "venueExistingSelected",
        "mapClickedLatLingUpdated",
        "userClickedMap",
    ],
    props: {
        venues: {
            type: Array,
            // eslint-disable-next-line vue/require-valid-default-prop
            default: [],
        },
        containerClass: {
            type: String,
            default: "",
        },
        showMap: {
            type: Boolean,
            default: true,
        },
        hideVenues: {
            type: Boolean,
            default: false,
        },
        hideMap: {
            type: Boolean,
            default: false,
        },
        existingVenues: {
            type: Array,
            // eslint-disable-next-line vue/require-valid-default-prop
            default: [],
        },
        showEnterManuallyButton: {
            type: Boolean,
            default: false,
        },
        showConfirmButton: {
            type: Boolean,
            default: true,
        },
        autocompleteError: {
            type: String,
            default: "",
        },
        existingVenueError: {
            type: String,
            default: "",
        },
        showAllFormLabels: {
            type: Boolean,
            default: false,
        },
        showSummaryOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            venueName: "",
            address1: "",
            address2: "",
            area: "",
            city: "",
            postcode: "",
            country: "United Kingdom",

            autocomplete: {},
            lat: "",
            lng: "",

            errors: {
                venueName: "",
                address1: "",
                address2: "",
                area: "",
                city: "",
                postcode: "",
                country: "",
            },

            allVenues: [],

            modalVenue: {
                idx: "",
                venueName: "",
                address1: "",
                address2: "",
                area: "",
                city: "",
                postcode: "",
                country: "United Kingdom",
            },

            manualAddress: false,
            formSubmited: false,
            venuesErrors: [],

            venueType: "new",
            showSuggestedVenues: false,
            suggestedVenue: "",
        };
    },
    methods: {
        ...mapActions(useMainStore, ["updateAdvancedVenueAddToSimple"]),
        selectSuggestedVenue(venue) {
            const params = new URLSearchParams(window.location.search);
            const fromMPSimpleAddVenue = params.get("fromAddVenueSimple");

            this.modalVenue = {
                venueName: venue.name,
                address1: venue.address,
                address2: "",
                area: venue.county,
                city: venue.city,
                postcode: venue.postcode,
                country: "United Kingdom",
                venueId: venue.venue_id,
                lat: venue.geo_latitude,
                lng: venue.geo_longitude,
            };

            this.suggestedVenue = venue.name;
            this.showSuggestedVenues = false;
            this.$emit("venueAdded", this.modalVenue, fromMPSimpleAddVenue === "yes");
            this.$emit("venueExistingSelected", this.modalVenue);

            if (fromMPSimpleAddVenue === "yes") { // moved below, better here possible race condition (if) above and dont want to force a wait -lm
                this.updateCopiedVenueAndRedirect(venue);
            }
        },

        // eslint-disable-next-line no-unused-vars
        getAddressData(addressData, placeResultData, id) {
            this.autocomplete = addressData;
            this.lat = addressData.latitude;
            this.lng = addressData.longitude;
            /*
            * if we selected a autocomplete suggestion from google , we can use that data instead of calling back via phalcon
            * via google which is slow the below (which be incorrect). Previously we used map.getParsedAddress
            */
            this.$refs.map.parseAddress(placeResultData.address_components);
        },
        replaceNA(area) {
            return area.replace("N/A", "");
        },
        updateLatLng({
            lat,
            lng,
            address,
            autocomplete,
        }) {
            this.lat = lat();
            this.lng = lng();

            this.venueName = "";
            this.address1 = address.address;
            this.area = this.replaceNA(address.county);
            this.city = address.city;
            this.postcode = address.postcode;
            this.$refs.autocomplete.update(autocomplete);
            this.$emit("mapClickedLatLingUpdated");
        },
        addressUpdated(address) {
            this.autocomplete = address;
            this.address1 = address.address;
            this.area = this.replaceNA(address.county);
            this.city = address.city;
            this.postcode = address.postcode ?? "";
        },
        confirmLocation() {
            if (!isEmpty(this.autocomplete)) {
                this.manualAddress = !this.manualAddress;
            }
        },
        enterManually() {
            this.manualAddress = !this.manualAddress;
            this.$emit("enterManuallyChanged", this.manualAddress);
        },
        saveVenue(skipReset = false) {
            this.validateForm();
            const errors = Object.values(this.errors).filter((el) => el !== "");
            if (errors.length) {
                return;
            }

            const venue = {
                venueName: this.venueName,
                address1: this.address1,
                address2: this.address2,
                area: this.area,
                city: this.city,
                postcode: this.postcode,
                country: this.country,
                lat: this.lat,
                lng: this.lng,
            };

            this.$emit("venueAdded", venue);

            if (!skipReset) {
                // Reset form
                this.venueName = "";
                this.address1 = "";
                this.address2 = "";
                this.area = "";
                this.city = "";
                this.postcode = "";
                this.country = "United Kingdom";
                this.lat = "";
                this.lng = "";

                this.$refs.autocomplete.clear();
                this.manualAddress = !this.manualAddress;
            }
        },
        prevStep() {
            this.$router.push({ name: "Listing details" });
        },
        validateForm() {
            this.errors.venueName = (this.venueName === "")
                ? "Enter a venue name"
                : "";
            this.errors.address1 = (this.address1 === "")
                ? "Enter an address"
                : "";
            this.errors.area = (this.area === "" || this.area === "N/A")
                ? "Enter an area"
                : "";
            this.errors.city = (this.city === "")
                ? "Enter a town/city"
                : "";
            this.errors.postcode = (this.postcode === "")
                ? "Enter a postcode"
                : "";
            this.errors.country = (this.country === "")
                ? "Enter a country"
                : "";

            const matchedVenus = this.venues.filter(
                (v) => v.venueName === this.venueName,
            ).length;

            this.errors.venueName = (matchedVenus > 0)
                ? "Venue name is already in use"
                : this.errors.venueName;

            this.checkAllFieldsForInvalidStrs();

            if (this.errors.postcode === "") { // if its blank skip this (as already handled)
                const validUkPostcodeFormat = validateUKPostcode(this.postcode);
                if (!validUkPostcodeFormat) {
                    this.errors.postcode = "This postcode is not valid";
                }
            }
            const errors = Object.values(this.errors).filter((el) => el !== "");
            const numErrors = errors.length;

            this.$emit("validatedSummaryFormNumErrors", numErrors);

            return errors.length <= 0;
        },
        checkAllFieldsForInvalidStrs() {
            const notAllowed = ["na", "n/a"]; // inputs that are not allowed

            const fieldsToCheck = {
                venueName: this.venueName,
                address1: this.address1,
                area: this.area,
                city: this.city,
                postcode: this.postcode,
                country: this.country,
            };

            Object.keys(fieldsToCheck).forEach(
                (fieldName) => {
                    const inputField = fieldsToCheck[fieldName].trim().toLowerCase();
                    const foundErrors = notAllowed.find((check) => inputField === check);
                    if (foundErrors !== undefined) {
                        this.errors[fieldName] = `This is not allowed: ${foundErrors}`;
                    }
                },
            );
        },
        closeModal() {
            this.modalVenue = {
                idx: "",
                venueName: "",
                address1: "",
                address2: "",
                area: "",
                city: "",
                postcode: "",
                country: "United Kingdom",
            };
        },
        updateVenueById(idx) {
            const venue = { ...this.modalVenue };
            delete venue.idx;
            // eslint-disable-next-line vue/no-mutating-props
            this.venues[idx] = venue;

            this.modalVenue = {
                idx: "",
                venueName: "",
                address1: "",
                address2: "",
                area: "",
                city: "",
                postcode: "",
                country: "United Kingdom",
            };

            this.$refs.closeModal.click();
            this.$emit("submitEditVenueModal");
        },
        setModalVenue(idx) {
            this.modalVenue = { idx, ...this.venues[idx] };
            this.$emit("openEditVenueModal");
        },
        removeVenue(idx) {
            // eslint-disable-next-line vue/no-mutating-props
            this.venues.splice(idx, 1);
        },
        userClickedTheMap() {
            this.$emit("userClickedMap");
        },
        updateCopiedVenueAndRedirect(venue) {
            const advancedVenueMakeAvailableOnSimple = {
                id: venue.venue_id,
            };
            const csrf = document.getElementById("csrf");
            window.onbeforeunload = null;
            this.updateAdvancedVenueAddToSimple(advancedVenueMakeAvailableOnSimple, csrf.value)
                .then(() => {
                    document.location.href = `/management-panel/simple/${advancedVenueMakeAvailableOnSimple.id}`;
                })
                .catch((error) => {
                    console.error(error.message);
                });
        },
    },
    computed: {
        newLocations() {
            return [...this.allVenues, ...this.venues];
        },
    },
    watch: {
        venueType(venueType) {
            this.$emit("venueTypeChange", venueType);
        },
    },
};
</script>

<template>
    <div v-if="!showSummaryOnly">
        <div v-if="existingVenues.length > 0" class="d-flex flex-column mb-3r">
            <div class="d-flex flex-row">
                <RadioInput
                    name="add-venue"
                    id="new-venue"
                    v-model="venueType"
                    value="new"
                    :selected="venueType === 'new'"
                >
                    Create a new venue
                </RadioInput>
            </div>
            <div class="d-flex flex-row">
                <RadioInput
                    name="add-venue"
                    id="existing-venue"
                    v-model="venueType"
                    value="existing"
                    :selected="venueType === 'existing'"
                >
                    Copy a previous venue
                </RadioInput>
            </div>
        </div>
    </div>

    <div v-if="existingVenues.length > 0 && venueType === 'existing'">

        <div class="mt-3 d-none flex-column" :class="{ 'd-flex': existingVenues.length > 0 }">
            <div class="position-relative">
                <div v-show="venueType === 'existing'" class="position-relative">
                    <input
                        @click="showSuggestedVenues = !showSuggestedVenues"
                        v-model="suggestedVenue"
                        class="w-100 text"
                        :class="{'error-input-border': existingVenueError !== ''}"
                        type="text" placeholder="Select a previous venue" readonly
                    />
                    <div class="arrow">
                        <svg class="animate" :class="{'animate-open': showSuggestedVenues}" width="19" height="19"
                             viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M3.63695 7.05328L8.8241 13.1066C8.90771 13.2041 9.01142 13.2824 9.12813 13.336C9.24484 13.3897 9.37177 13.4175 9.50023 13.4175C9.62868 13.4175 9.75562 13.3897 9.87233 13.336C9.98903 13.2824 10.0927 13.2041 10.1764 13.1066L15.3635 7.05328C15.8585 6.47549 15.4481 5.58301 14.6874 5.58301H4.3116C3.55086 5.58301 3.14043 6.47549 3.63695 7.05328Z"
                                fill="#FFB700"/>
                        </svg>
                    </div>
                </div>

                <div v-show="showSuggestedVenues" class="added-classes-container">
                    <div
                        @click="selectSuggestedVenue(single)"
                        class="cursor-pointer ml-3 added-item"
                        v-for="(single, key) in existingVenues"
                        :key="key"
                    >
                        <span class="added-class-name">{{ single.name }}</span>
                        <span class="added-class-age-groups">{{ single.address }}, {{ single.city }},
                            {{ single.postcode }}</span>
                    </div>
                </div>

                <div class="error mt-2" :class="{'show': existingVenueError !== ''}">
                    {{ existingVenueError }}
                </div>

            </div>
        </div>
    </div>

    <div :class="{'mt-3': !hideVenues}">
        <div
            v-show="!hideVenues"
            v-for="(venue, idx) in newLocations"
            :key="idx"
            class="mt-2 venue-item"
        >
            <div class="actions">
                <div @click="setModalVenue(idx)" data-toggle="modal" data-target="#editModal" class="edit">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M1.57784 11.5044C1.49691 11.5042 1.41689 11.4873 1.34288 11.4545C1.26886 11.4218 1.20247 11.374 1.14792 11.3142C1.09239 11.2549 1.05001 11.1846 1.02357 11.1078C0.997133 11.031 0.987228 10.9495 0.994506 10.8685L1.13742 9.29705L7.73959 2.69722L9.80284 4.75988L3.20242 11.3591L1.63092 11.502C1.61328 11.5037 1.59556 11.5045 1.57784 11.5044ZM10.2147 4.34747L8.15201 2.2848L9.38926 1.04755C9.44343 0.993313 9.50777 0.950286 9.57858 0.920931C9.6494 0.891575 9.72531 0.876465 9.80196 0.876465C9.87862 0.876465 9.95453 0.891575 10.0253 0.920931C10.0962 0.950286 10.1605 0.993313 10.2147 1.04755L11.4519 2.2848C11.5062 2.33897 11.5492 2.40331 11.5785 2.47413C11.6079 2.54494 11.623 2.62085 11.623 2.69751C11.623 2.77417 11.6079 2.85007 11.5785 2.92089C11.5492 2.9917 11.5062 3.05604 11.4519 3.11022L10.2153 4.34688L10.2147 4.34747Z"
                            fill="#260045"
                        />
                    </svg>
                    Edit
                </div>
                <div @click="removeVenue(idx)" class="ml-2 remove">
                    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_104:15033)">
                            <path
                                d="M1.75 4.875V13.625C1.75 14.1062 2.14375 14.5 2.625 14.5H10.5C10.9812 14.5 11.375 14.1062 11.375 13.625V4.875H1.75ZM4.375 12.75H3.5V6.625H4.375V12.75ZM6.125 12.75H5.25V6.625H6.125V12.75ZM7.875 12.75H7V6.625H7.875V12.75ZM9.625 12.75H8.75V6.625H9.625V12.75Z"
                                fill="#EB4247"
                            />
                            <path
                                d="M11.5938 2.25H8.75V1.15625C8.74931 0.982414 8.67995 0.815896 8.55702 0.692975C8.4341 0.570054 8.26759 0.500691 8.09375 0.5H5.03125C4.85741 0.500691 4.6909 0.570054 4.56797 0.692975C4.44505 0.815896 4.37569 0.982414 4.375 1.15625V2.25H1.53125C1.35734 2.25046 1.19069 2.31975 1.06772 2.44272C0.944751 2.56569 0.875462 2.73234 0.875 2.90625V4H12.25V2.90625C12.2495 2.73234 12.1802 2.56569 12.0573 2.44272C11.9343 2.31975 11.7677 2.25046 11.5938 2.25ZM7.875 2.25H5.25V1.38638H7.875V2.25Z"
                                fill="#EB4247"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_104:15033">
                                <rect width="14" height="14" fill="white" transform="translate(0 0.5)"/>
                            </clipPath>
                        </defs>
                    </svg>
                    Remove
                </div>
            </div>
            <div class="title">{{ venue.venueName }} {{ ", " + venue.area }}</div>
            <div class="mt-2 address">Address: {{ venue.address1 }}, {{ venue.city }}, {{ venue.postcode }}</div>
            <div class="error show" v-if="venuesErrors[idx]">
                <div
                    v-for="(error, key) in Object.values(venuesErrors[idx])"
                    :key="key"
                >
                    {{ error }}
                </div>
            </div>
        </div>
    </div>

    <!-- Modal window -->
    <div class="modal fade" id="editModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalCenterTitle"
         aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content modal-lg">
                <div class="modal-header">
                    <h5 class="modal-title" id="exampleModalLongTitle">Edit venue</h5>
                    <button @click="closeModal" type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="mt-4 manual-address-container">
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.venueName.length }"
                            >Venue name</label>
                            <input
                                class="text"
                                type="text"
                                v-model="modalVenue.venueName"
                                placeholder="Venue name"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.address1.length }"
                            >Address</label>
                            <input
                                class="text"
                                type="text"
                                v-model="modalVenue.address1"
                                placeholder="Address line 1"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.address2.length }"
                            >Address line 2</label>
                            <input
                                class="text"
                                type="text"
                                v-model="modalVenue.address2"
                                placeholder="Address line 2"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.area.length }"
                            >Area / Town</label>
                            <input
                                class="text"
                                v-model="modalVenue.area"
                                type="text"
                                placeholder="Area"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.city.length }"
                            >City / County</label>
                            <input
                                class="text"
                                v-model="modalVenue.city"
                                type="text"
                                placeholder="City"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label
                                :class="{ 'show': modalVenue.postcode.length }"
                            >Post code</label>
                            <input
                                class="text"
                                v-model="modalVenue.postcode"
                                type="text"
                                placeholder="Post code"
                            />
                        </div>
                        <div class="mt-4 d-flex flex-column">
                            <label :class="{ 'show': modalVenue.country.length }">Country</label>
                            <input
                                class="text"
                                v-model="modalVenue.country"
                                type="text"
                                placeholder="Postcode"
                                disabled
                            />
                        </div>
                    </div>
                    <div class="modal-footer">
                        <a
                            @click="closeModal"
                            ref="closeModal"
                            type="button"
                            class="link"
                            data-dismiss="modal"
                        >
                            Close
                        </a>
                        <button
                            type="button"
                            class="btn btn-new"
                            @click="updateVenueById(modalVenue.idx)"
                        >
                            Update venue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div
        v-if="!hideMap && venueType !== 'existing'"
        v-show="showMap"
        :class="{[containerClass]: true, autocompletespacing: venueType !== 'existing' }"
    >
        <label v-if="!manualAddress" class="show autocomplete-label">Address</label>
        <div v-if="!manualAddress" class="position-relative">
            <div v-if="showEnterManuallyButton && venueType !== 'existing'"
                 @click="enterManually"
                 class="enter-manually action-text font-weight-bold cursor-pointer"
                 data-test="enter-address-manually">
                Enter address manually
            </div>
        </div>

        <!--  Google autocomplete      -->
        <vue-google-autocomplete
            v-show="!manualAddress"
            ref="autocomplete"
            id="map"
            classname="w-100 text autocomplete-field"
            :class="{
                'error-input-border': autocompleteError !== ''
            }"
            placeholder="Search for an address"
            @placechanged="getAddressData"
            :fields="['address_components', 'geometry']"
            :types="'geocode'"
            :country="['uk']"
        />

        <div
            class="error"
            :class="{'show': autocompleteError !== ''}"
        >
            {{ autocompleteError }}
        </div>

        <!--  Google map      -->
        <div v-show="!manualAddress && venueType !== 'existing'">
            <google-map
                ref="map"
                id="map"
                @locationChange="updateLatLng"
                @addressUpdated="addressUpdated"
                @userClickedMap="userClickedTheMap"
                :lat="lat"
                :lng="lng"
                class="mt-4"
            />
        </div>

        <div v-show="manualAddress" class="mt-4 manual-address-container">
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Venue name"
                    placeholder="Enter a venue name"
                    :maxlength="65"
                    :required="true"
                    v-model="venueName"
                    :value="venueName"
                    :error="errors.venueName"
                    :testId="'summary-venue-name-input'"
                    dataTest="input-venue-name"
                />
            </div>
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Address"
                    placeholder="Address"
                    :maxlength="65"
                    :required="true"
                    v-model="address1"
                    :value="address1"
                    :error="errors.address1"
                    :testId="'summary-venue-address-input'"
                    dataTest="input-venue-address"
                />
            </div>
            <div v-if="false" class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Address line 2"
                    placeholder="Address line 2"
                    :maxlength="65"
                    :required="true"
                    v-model="address2"
                    :value="address2"
                />
            </div>
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Area"
                    placeholder="Enter an area"
                    :maxlength="65"
                    :required="true"
                    v-model="area"
                    :value="area"
                    :error="errors.area"
                    sub-label-text="Please enter an area name. This will help your customers
                    easily differentiate between multiple venues of your business."
                    :testId="'summary-venue-area-input'"
                    dataTest="input-venue-area"
                />
            </div>
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Town/City"
                    placeholder="Enter a town/city"
                    :maxlength="65"
                    :required="true"
                    v-model="city"
                    :value="city"
                    :error="errors.city"
                    :testId="'summary-venue-city-input'"
                    dataTest="input-venue-city"
                />
            </div>
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Postcode"
                    placeholder="Enter a postcode"
                    :maxlength="65"
                    :required="true"
                    v-model="postcode"
                    :value="postcode"
                    :error="errors.postcode"
                    :testId="'summary-venue-postcode-input'"
                    dataTest="input-venue-postcode"
                />
            </div>
            <div class="mt-3 d-flex flex-column">
                <InputField
                    labelText="Country"
                    placeholder="Enter country name"
                    :maxlength="65"
                    :required="true"
                    v-model="country"
                    :value="country"
                    :error="errors.country"
                    :disabled="true"
                />
            </div>
        </div>
    </div>

    <div
        v-if="showConfirmButton && showMap && !hideMap"
        class="d-flex flex-row justify-content-end"
    >
        <button
            v-if="!manualAddress"
            v-show="!manualAddress"
            @click="confirmLocation"
            class="btn-new btn-new-outline mt-4 confirm-location"
        >
            Confirm location
        </button>
        <button
            v-show="manualAddress"
            class="btn-new btn-new-outline mt-4 ml-0"
            @click="manualAddress = !manualAddress"
        >
            Back
        </button>
        <button
            v-show="manualAddress"
            @click="saveVenue"
            class="btn-new btn-new-outline mt-4 save_venue"
        >
            Save venue
        </button>
    </div>
</template>

<style scoped>

.autocompletespacing {
    margin-top: 3rem;
}

.autocomplete-label {
    font-size: 12px;
}

.autocomplete-field {
    font-size: 14px;
    padding: 12px 16px 12px 16px;
}

.redstar {
    color: red;
}

.under_label_note {
    font-family: 'Poppins';
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    /* or 157% */
    /* Body Grey */
    color: #636066;
}

.enter-manually {
    position: absolute;
    top: -25px;
    right: 0;
}

.mb-3r {
    margin-top: 1.5rem;
    margin-bottom: 3rem;
}

.arrow {
    pointer-events: none;
}

.animate {
    transition: transform 0.15s cubic-bezier(1, 0.5, 0.8, 1);
    transition-timing-function: cubic-bezier(1, 0.5, 0.8, 1);
}

.animate-open {
    transform: rotate(180deg);
}

.added-classes-container {
    top: 47px;
    position: absolute;
    padding: 8px;
    min-width: 466px;
    min-height: 30px;
    max-height: 172px;
    background: #FFFFFF;
    border: 1px solid #DDDDDD;
    box-sizing: border-box;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    width: 100%;
    overflow-y: scroll;
    z-index: 3;
}
</style>
